import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import MathText from '../VueQuestion/MathText';
import { normaliserNom, attendusDuNiveau } from '../../utils/collerNotes';
import { chercherAttendus } from '../../utils/rechercheAttendus';
import { useThemeEnCache } from '../../hooks/fetch_api_hook/useThemeEnCache';
import PastilleRubrique, { RUBRIQUES, rubriqueDe } from './PastilleRubrique';
import PastilleNiveau, { chiffreNiveau } from './PastilleNiveau';

// Ordre des deux groupes d'un chapitre : les objectifs d'abord.
const ORDRE_RUBRIQUES = ['objectif', 'automatisme'];

// Les niveaux proposés par le petit menu « Niveau » (chiffres de themes/?niveau=).
const NIVEAUX = ['6', '5', '4', '3'];

// Lignes d'attente du premier chargement d'un niveau (largeurs variées, fixes).
const LIGNES_ATTENTE = ['w-1/3', 'w-5/6', 'w-2/3', 'w-3/4', 'w-1/2', 'w-11/12', 'w-2/5', 'w-4/5', 'w-3/5', 'w-1/4'];

/**
 * Menu de choix d'un attendu de « Coller mes notes », le MÊME pour les deux
 * collages (2026-09-29) : une ligne du tableau des colonnes de compétences,
 * ou « Ajouter un attendu… » quand on colle une note seule. DEUX chemins dans
 * le même menu :
 *   - reconnaissance : les chapitres du niveau, rangés sous leur thème, puis
 *     les attendus du chapitre choisi (deux clics : le niveau des thèmes a été
 *     retiré le 2026-09-29), pour le prof qui ne se rappelle plus le libellé ;
 *   - recherche : l'input du haut ; taper bascule en résultats à plat sur
 *     tout le niveau et court-circuite les chapitres. Recherche par MOTS
 *     (rechercheAttendus) : dans n'importe quel ordre, petits mots ignorés,
 *     les attendus qui ont tous les mots d'abord ; repli sur la suite de
 *     lettres quand elle ne trouve rien (un mot de moins de quatre lettres en
 *     cours de frappe : « pér » trouve déjà « périmètre »).
 * La recherche s'ouvre VIDE : le pré-remplissage par le titre de la colonne a
 * été retiré le 2026-09-29 (des titres Pronote trop larges proposaient de
 * mauvais candidats). Le menu s'ouvre TOUJOURS vers le bas, à une seule
 * hauteur ; à l'ouverture, le navigateur fait défiler la page juste assez pour
 * le montrer en entier (scrollIntoView « nearest » : rien ne bouge s'il est
 * déjà visible). Ça remplace, le 2026-09-29, un calcul qui l'ouvrait vers le
 * haut en bas de page (jugé bizarre). La liste revient en haut à chaque frappe.
 * `onVider` (facultatif) ajoute en tête « Ne pas importer cette colonne » quand
 * un attendu est choisi. Le menu s'ouvre TOUJOURS sur la liste des chapitres :
 * la mémoire du dernier chapitre visité a été retirée le 2026-09-29 (prof :
 * les chapitres changent d'une évaluation à l'autre). Fermeture au clic
 * extérieur et à Échap.
 *
 * AUTRE NIVEAU (2026-09-29, demande de deux profs : évaluer une 4e sur un
 * objectif de 6e ; rare, donc discret) : avec `niveauClasse`, un petit menu
 * « Niveau » à côté de la recherche, réglé sur la classe à chaque ouverture.
 * Choisir un autre niveau charge SON arbre (themes/?niveau=, un niveau à la
 * fois) ; recherche et chapitres portent alors sur lui. L'attendu choisi garde
 * son niveau ; la ligne affiche « 6e » quand il n'est pas celui de la classe.
 */
const ChoixAttendu = ({
    valeur, onChange, themes = [], attendus = [],
    onVider = null, libelleVide = 'Choisir un attendu…', niveauClasse = '',
}) => {
    const classe = chiffreNiveau(niveauClasse);
    const [ouvert, setOuvert] = useState(false);
    const [recherche, setRecherche] = useState('');
    const [themeNom, setThemeNom] = useState(null);
    const [chapitreNom, setChapitreNom] = useState(null);
    const [niveau, setNiveau] = useState(classe);
    const racineRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const listeRef = useRef(null);

    // L'arbre d'un autre niveau n'est chargé que menu ouvert, sur ce niveau-là,
    // une seule fois par page (useThemeEnCache).
    const autreNiveau = ouvert && Boolean(niveau) && niveau !== classe;
    const { data: themesAutre, isLoading: chargementAutre } = useThemeEnCache(niveau, autreNiveau);
    const themesActifs = useMemo(
        () => (autreNiveau ? (themesAutre || []) : themes),
        [autreNiveau, themesAutre, themes],
    );
    const attendusActifs = useMemo(
        () => (autreNiveau ? attendusDuNiveau(themesAutre) : attendus),
        [autreNiveau, themesAutre, attendus],
    );

    const ouvrir = () => {
        setRecherche('');
        setThemeNom(null);
        setChapitreNom(null);
        setNiveau(classe);
        setOuvert(true);
    };

    const changerNiveau = (n) => {
        setNiveau(n);
        setThemeNom(null);
        setChapitreNom(null);
    };

    // La liste repart du haut à l'ouverture, à chaque frappe et à chaque
    // changement de chapitre ou de niveau : le meilleur résultat est visible.
    useEffect(() => {
        if (ouvert && listeRef.current) listeRef.current.scrollTop = 0;
    }, [ouvert, recherche, themeNom, chapitreNom, niveau]);

    useEffect(() => {
        if (!ouvert) return undefined;
        const fermerSiDehors = (e) => {
            if (racineRef.current && !racineRef.current.contains(e.target)) setOuvert(false);
        };
        const fermerEchap = (e) => {
            if (e.key === 'Escape') setOuvert(false);
        };
        document.addEventListener('mousedown', fermerSiDehors);
        document.addEventListener('keydown', fermerEchap);
        // Le focus ne fait pas défiler la page ; c'est le menu entier que le
        // navigateur amène à l'écran, s'il en sortait (sinon rien ne bouge).
        inputRef.current?.focus({ preventScroll: true });
        menuRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        return () => {
            document.removeEventListener('mousedown', fermerSiDehors);
            document.removeEventListener('keydown', fermerEchap);
        };
    }, [ouvert]);

    const theme = useMemo(() => (themesActifs || []).find((t) => t.nom === themeNom) || null, [themesActifs, themeNom]);
    const chapitre = useMemo(
        () => (theme?.descripteur || []).find((d) => d.nom === chapitreNom) || null,
        [theme, chapitreNom]
    );

    const resultatsRecherche = useMemo(() => {
        const q = normaliserNom(recherche);
        if (!q) return null;
        const parMots = chercherAttendus(themesActifs, recherche, { max: 30 }).map((r) => ({
            id: r.id, description: r.description, rubrique: r.rubrique, niveau: r.niveau,
            themeNom: r.theme, descripteurNom: r.descripteur,
        }));
        if (parMots.length) return parMots;
        return attendusActifs.filter((a) => normaliserNom(a.description).includes(q));
    }, [themesActifs, attendusActifs, recherche]);

    const choisir = (a) => {
        onChange(a);
        setOuvert(false);
    };

    const LigneChoix = ({ onClick, children }) => (
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center gap-2 text-left px-3 py-1 text-[12.5px] font-semibold text-slate-600 leading-tight border-b border-slate-50 last:border-b-0 hover:bg-blue-50 transition-colors"
        >
            {children}
        </button>
    );

    return (
        <div ref={racineRef} className="relative min-w-0">
            <button
                type="button"
                onClick={() => (ouvert ? setOuvert(false) : ouvrir())}
                // Vide : gris en pointillés, plus l'orange « à faire » (2026-09-29) :
                // une colonne peut rester vide, elle n'est alors pas importée.
                className={`w-full flex items-center gap-2 border-2 rounded-xl px-3 py-2 text-left text-[13px] font-semibold transition-colors ${
                    valeur
                        ? 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'
                        : 'border-dashed border-slate-300 bg-slate-50 text-slate-500 hover:border-blue-300'
                }`}
            >
                <span className="flex-1 min-w-0 truncate">
                    {valeur ? <MathText>{valeur.description}</MathText> : libelleVide}
                </span>
                {valeur ? <PastilleNiveau attendu={valeur} niveauClasse={classe} /> : null}
                {valeur ? <PastilleRubrique attendu={valeur} /> : null}
                <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-60" />
            </button>

            {ouvert && (
                // Le bloc que le navigateur amène à l'écran : le menu, plus une marge
                // transparente de 32 px dessous (le menu ne colle plus au bas de la
                // fenêtre, retour prof du 2026-09-29 ; elle ne capte aucun clic).
                <div ref={menuRef} className="absolute z-40 top-full mt-1.5 left-0 right-0 min-w-[320px]">
                <div className="bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_rgba(15,23,42,0.16)] overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 bg-slate-50/60">
                        <Search className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={recherche}
                            onChange={(e) => setRecherche(e.target.value)}
                            placeholder="Tapez des mots de l'attendu : comparer décimaux, périmètre…"
                            className="w-full text-[13px] font-semibold outline-none bg-transparent"
                        />
                        {/* Autre niveau : discret, réglé sur la classe à chaque ouverture. */}
                        {classe ? (
                            <select
                                aria-label="Niveau des attendus"
                                value={niveau}
                                onChange={(e) => changerNiveau(e.target.value)}
                                className="flex-shrink-0 text-[12px] font-bold text-slate-600 bg-white border border-slate-200 rounded-lg px-1.5 py-1 outline-none focus:border-blue-400"
                            >
                                {NIVEAUX.map((n) => (
                                    <option key={n} value={n}>{n}e{n === classe ? ' (classe)' : ''}</option>
                                ))}
                            </select>
                        ) : null}
                    </div>

                    {onVider && valeur ? (
                        <button
                            type="button"
                            onClick={() => { onVider(); setOuvert(false); }}
                            className="w-full text-left px-3 py-2 text-[12.5px] font-extrabold text-slate-500 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        >
                            Ne pas importer cette colonne
                        </button>
                    ) : null}

                    <div ref={listeRef} className="max-h-80 overflow-y-auto">
                        {autreNiveau && chargementAutre ? (
                            // Premier chargement d'un niveau : des lignes grises à la
                            // hauteur de la liste, le menu ne saute pas.
                            <div role="status" aria-label={`Chargement des attendus de ${niveau}e`} className="h-80 px-3 py-2.5 space-y-2.5 overflow-hidden">
                                {LIGNES_ATTENTE.map((largeur) => (
                                    <div key={largeur} className={`h-4 rounded bg-slate-100 animate-pulse ${largeur}`} />
                                ))}
                            </div>
                        ) : resultatsRecherche ? (
                            // ── Recherche à plat sur tout le niveau ──
                            resultatsRecherche.length === 0 ? (
                                <p className="px-3 py-3 text-[12.5px] font-semibold text-slate-400">Aucun attendu ne correspond.</p>
                            ) : (
                                resultatsRecherche.map((a) => (
                                    <LigneChoix
                                        key={a.id}
                                        onClick={() => choisir(a)}
                                    >
                                        {/* Le chapitre suit le libellé sur la même ligne :
                                            plus de résultats visibles (revue du 2026-09-29). */}
                                        <span className="min-w-0 flex-1">
                                            <MathText>{a.description}</MathText>
                                            <span className="text-[11px] font-medium text-slate-400"> · {a.descripteurNom}</span>
                                        </span>
                                        <PastilleRubrique attendu={a} />
                                    </LigneChoix>
                                ))
                            )
                        ) : !chapitre ? (
                            // ── Les chapitres du niveau, rangés sous leur thème : un clic
                            //    sur le chapitre, un sur l'attendu (2026-09-29) ──
                            (themesActifs || []).map((t) => (
                                <div key={t.id}>
                                    <p className="px-3 pt-2.5 pb-1 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">
                                        {t.nom}
                                    </p>
                                    {(t.descripteur || []).map((d) => (
                                        <LigneChoix key={d.id} onClick={() => { setThemeNom(t.nom); setChapitreNom(d.nom); }}>
                                            <span className="flex-1">{d.nom}</span>
                                            <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                        </LigneChoix>
                                    ))}
                                </div>
                            ))
                        ) : (
                            // ── Les attendus du chapitre ──
                            <>
                                <button
                                    type="button"
                                    onClick={() => { setThemeNom(null); setChapitreNom(null); }}
                                    className="w-full flex items-center gap-1.5 px-3 py-2 text-[12px] font-extrabold text-blue-700 bg-blue-50 hover:bg-blue-100 border-b border-blue-100"
                                >
                                    <ChevronLeft className="w-4 h-4" /> Retour · {chapitre.nom}
                                </button>
                                {(chapitre.attendus || []).length === 0 ? (
                                    <p className="px-3 py-3 text-[12.5px] font-semibold text-slate-400">Aucun attendu dans ce chapitre.</p>
                                ) : (
                                    // Deux groupes, chacun sous son intertitre en couleur :
                                    // objectifs d'apprentissage, puis automatismes (2026-09-29).
                                    ORDRE_RUBRIQUES.map((rub) => {
                                        const liste = chapitre.attendus.filter((a) => rubriqueDe(a) === rub);
                                        if (liste.length === 0) return null;
                                        return (
                                            <div key={rub}>
                                                <p className={`px-3 pt-2.5 pb-1 text-[11px] font-extrabold uppercase tracking-wide ${RUBRIQUES[rub].intertitre}`}>
                                                    {RUBRIQUES[rub].titre}
                                                </p>
                                                {liste.map((a) => (
                                                    <LigneChoix
                                                        key={a.id}
                                                        onClick={() => choisir(
                                                            {
                                                                id: a.id, description: a.description, rubrique: a.rubrique, niveau: a.niveau,
                                                                themeNom: theme.nom, descripteurNom: chapitre.nom,
                                                            },
                                                        )}
                                                    >
                                                        <span className={valeur?.id === a.id ? 'text-blue-800 font-bold' : ''}>
                                                            <MathText>{a.description}</MathText>
                                                        </span>
                                                    </LigneChoix>
                                                ))}
                                            </div>
                                        );
                                    })
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div aria-hidden="true" className="h-8 pointer-events-none" />
                </div>
            )}
        </div>
    );
};

export default ChoixAttendu;
